import { useCallback } from 'react';

import { OperationsFilterParams } from 'api/types/data/operation';
import { PermissionType } from 'api/types/entity';
import { ContentHeader, SearchBarWithFilter } from 'components';
import { ButtonNavigationLink } from 'components/ButtonNavigationLink/ButtonNavigationLink';
import { lodash } from 'helpers';
import { useTranslation } from 'libs/i18n';
import { ROUTES } from 'libs/navigation';
import { PermissionWrapper } from 'libs/permissions';
import { Stack } from 'libs/ui';
import { Icon } from 'libs/ui/Icon';

import {
  OperationsFilters,
  OperationsTable,
  type OperationsTableProps,
} from '../components';

interface OperationsViewProps extends OperationsTableProps {
  filter: OperationsFilterParams;
  setFilter: (filter?: OperationsFilterParams) => void;
}

export const OperationsView = ({
  filter,
  setFilter,
  ...tableProps
}: OperationsViewProps) => {
  const { t } = useTranslation();

  const { search } = filter;

  const countFilter =
    lodash.defaultTo([filter?.orderStatuses?.length], 0) +
    lodash.defaultTo([filter?.executionTimestamps?.length], 0) +
    lodash.defaultTo([filter?.orderEndTimestamps?.length], 0) +
    lodash.defaultTo([filter?.traderIds?.length], 0);

  const noRowsLabel =
    (!!countFilter && t('components.table.filterEmptyData')) ||
    (!!search && t('components.table.searchEmptyData')) ||
    t('components.table.emptyData');

  const handleApply = useCallback(
    (newFilter: OperationsFilterParams) => {
      setFilter({ search, ...newFilter });
    },
    [search, setFilter]
  );

  return (
    <Stack spacing={24}>
      <ContentHeader
        header={t('operation.operations.title')}
        rightContent={
          <Stack direction="row" flexWrap="wrap" spacing={12}>
            <SearchBarWithFilter
              filterContent={
                <OperationsFilters filter={filter} onApply={handleApply} />
              }
              search={search ?? ''}
              onSearch={(value) => setFilter({ ...filter, search: value })}
            />
            <PermissionWrapper
              permissions={[PermissionType.Operation_CreateOneViaList]}
            >
              <ButtonNavigationLink
                fullWidth={false}
                label={t('common.add')}
                sizeVariant="s"
                startIcon={<Icon.Plus size={16} />}
                to={ROUTES.operations.createOperation.fullPath}
              />
            </PermissionWrapper>
          </Stack>
        }
      />
      <OperationsTable {...tableProps} noRowsLabel={noRowsLabel} />
    </Stack>
  );
};
